import React, { lazy, Suspense, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import Navbar from "@/components/Navbar";
import { SessionProvider } from "@/context/SessionContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/auth/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";

const Pricing = lazy(() => import("@/pages/Pricing"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Contact = lazy(() => import("@/pages/Contact"));
const LaunchKit = lazy(() => import("@/pages/LaunchKit"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SignInDialog = lazy(() => import("@/components/auth/SignInDialog").then((module) => ({ default: module.SignInDialog })));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-ink text-mist gap-4 px-6 text-center">
          <div className="w-3 h-3 rounded-full bg-neon pulse-dot" data-testid="error-dot" />
          <p className="font-display text-2xl text-white">Something glitched.</p>
          <button onClick={() => window.location.reload()} className="px-6 py-3 rounded-full bg-neon text-ink font-semibold hover:bg-neonhover transition-colors" data-testid="error-reload-button">
            Reload Jiggly
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

function AppContent() {
  const { hash } = useLocation();
  const { prompt } = useAuth();
  // Handle the one-time OAuth fragment before rendering normal routes or the tool.
  if (new URLSearchParams(hash.slice(1)).has("session_id")) return <AuthCallback />;
  return (
    <>
        <ScrollManager />
        <div className="min-h-screen bg-ink text-white font-body">
          <Navbar />
          <main>
            <Suspense fallback={<div role="status" className="min-h-[60vh] pt-40 px-6 text-center text-mist" data-testid="page-loading">Loading…</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/launch-kit" element={<LaunchKit />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          <Footer />
          <Suspense fallback={null}>{prompt && <SignInDialog />}</Suspense>
          <Toaster theme="dark" position="bottom-right" data-testid="app-notifications" />
        </div>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SessionProvider>
            <AppContent />
          </SessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
